import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import apiClient from '../../utils/apiClient';

// 認証ヘッダー付きのaxiosインスタンスを作成
const createAuthAxios = () => {
  const token = localStorage.getItem('waton_token');

  // apiClientをベースに新しいインスタンスを作成
  const instance = axios.create({
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    withCredentials: true
  });

  // リクエストインターセプターでURLを設定
  instance.interceptors.request.use((config) => {
    // apiClientと同じ方式でURLを構築
    if (config.url && !config.url.startsWith('http')) {
      // 環境変数を最優先
      if (import.meta.env.VITE_API_URL) {
        config.url = import.meta.env.VITE_API_URL + config.url;
      } else {
        // apiClient.tsのgetApiUrl相当の処理
        const hostname = window.location.hostname;
        const protocol = window.location.protocol;

        // line.wa-ton.comの場合はHTTPS
        if (hostname === 'line.wa-ton.com') {
          config.url = 'https://line.wa-ton.com/api' + config.url;
        } else if (protocol === 'https:') {
          // HTTPSページの場合はHTTPSを使用（ポートなし）
          config.url = `https://${hostname}/api` + config.url;
        } else {
          // HTTPの場合のみポート3001を使用
          config.url = `http://${hostname}:3001/api` + config.url;
        }
      }
    }
    return config;
  });

  return instance;
};
import {
  Users, UserPlus, Edit, Trash2, Lock, Mail, Phone, Calendar,
  Shield, UserCheck, AlertCircle, Palette, Key, CheckSquare,
  Camera, Upload, X, Home, Eye, EyeOff
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../layout/PageContainer';

interface Staff {
  id: number;
  username: string;
  display_name: string;
  name: string;
  role: string;
  role_level: number;
  active: boolean;
  icon: string;
  icon_color: string;
  thumbnail_url?: string;
  enable_pin_login: boolean;
  has_pin: boolean;
  email?: string;
  phone?: string;
  created_at: string;
  updated_at: string;
}

interface Permission {
  page_path: string;
  can_access: boolean;
}

interface PageOption {
  path: string;
  label: string;
}

interface PermissionItem {
  id: number;
  permission_key: string;
  display_name: string;
  description: string;
  category: string;
  route_path: string;
  icon: string;
  display_order: number;
  requires_admin: number;
  requires_developer: number;
}

interface PermissionTemplate {
  id: number;
  template_name: string;
  description: string;
  icon: string;
  display_order: number;
}

interface StaffFormData {
  username: string;
  password: string;
  name: string;
  role_level: number;
  pin?: string;
  icon: string;
  icon_color: string;
  thumbnail_url?: string;
  enable_pin_login: boolean;
  permissions: Permission[];
}

const availableIcons = [
  '👨‍💼', '👩‍💼', '👨‍🍳', '👩‍🍳', '👨‍🎓', '👩‍🎓',
  '🧑‍💻', '👷‍♂️', '👷‍♀️', '🦸‍♂️', '🦸‍♀️', '🧙‍♂️',
  '🐯', '🦁', '🐶', '🐱', '🦊', '🐻', '🐼', '🐨',
  '⭐', '🌟', '💫', '🔥', '❤️', '💙', '💚', '💛'
];

const iconColors = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
  '#EC4899', '#06B6D4', '#6366F1', '#F97316', '#84CC16'
];

const StaffManagementEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [permissionItems, setPermissionItems] = useState<PermissionItem[]>([]);
  const [permissionTemplates, setPermissionTemplates] = useState<PermissionTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'list' | 'permissions' | 'icons'>('list');
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<StaffFormData>({
    username: '',
    password: '',
    name: '',
    role_level: 0,
    pin: '',
    icon: '👤',
    icon_color: '#3B82F6',
    enable_pin_login: true,
    permissions: []
  });

  const [passwordResetForm, setPasswordResetForm] = useState({
    staffId: 0,
    newPassword: '',
    confirmPassword: ''
  });

  const [pinResetForm, setPinResetForm] = useState({
    staffId: 0,
    newPin: '',
    confirmPin: ''
  });

  useEffect(() => {
    fetchStaffList();
    fetchAvailableIcons();
    fetchAvailablePages();
    fetchPermissionItems();
    fetchPermissionTemplates();
  }, []);

  const fetchStaffList = async () => {
    setLoading(true);
    try {
      const apiClient = createAuthAxios();
      const response = await apiClient.get('/staff');
      if (response.data.success) {
        console.log('取得したスタッフデータ:', response.data.data);
        response.data.data.forEach((staff: any) => {
          console.log(`${staff.displayName}: roleLevel=${staff.roleLevel}, roleLabel=${staff.roleLabel}`);
          if (staff.thumbnail_url) {
            console.log(`${staff.displayName}のthumbnail_url:`, staff.thumbnail_url.substring(0, 100));
          }
        });
        setStaffList(response.data.data);
      }
    } catch (error) {
      console.error('スタッフリスト取得エラー:', error);
      alert('スタッフリストの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // アイコン設定用エンドポイント
  const fetchAvailableIcons = async () => {
    try {
      const apiClient = createAuthAxios();
      const response = await apiClient.get('/staff/config/icons');
      if (response.data.success && response.data.data) {
        // アイコンとカラーのリストを状態に保存（必要に応じて）
        console.log('利用可能なアイコン:', response.data.data);
      }
    } catch (error) {
      console.error('アイコンリスト取得エラー:', error);
    }
  };

  // ページ設定用エンドポイント
  const fetchAvailablePages = async () => {
    try {
      const apiClient = createAuthAxios();
      const response = await apiClient.get('/staff/config/pages');
      if (response.data.success && response.data.data) {
        // ページリストを状態に保存（必要に応じて）
        console.log('利用可能なページ:', response.data.data);
      }
    } catch (error) {
      console.error('ページリスト取得エラー:', error);
    }
  };

  const fetchPermissionItems = async () => {
    try {
      const response = await apiClient.get('/permissions/items');
      if (response.data.success && response.data.data) {
        setPermissionItems(response.data.data);
        console.log('権限項目取得成功:', response.data.data.length, '件');
      }
    } catch (error) {
      console.error('権限項目取得エラー:', error);
    }
  };

  const fetchPermissionTemplates = async () => {
    try {
      const response = await apiClient.get('/permissions/templates');
      if (response.data.success && response.data.data) {
        setPermissionTemplates(response.data.data);
        console.log('テンプレート取得成功:', response.data.data.length, '件');
      }
    } catch (error) {
      console.error('テンプレート取得エラー:', error);
    }
  };

  const fetchStaffDetails = async (staffId: number) => {
    try {
      const apiClient = createAuthAxios();
      const response = await apiClient.get(`/staff/${staffId}`);
      if (response.data.success) {
        return response.data.data;
      }
    } catch (error) {
      console.error('スタッフ詳細取得エラー:', error);
      return null;
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 画像ファイルチェック
    if (!file.type.startsWith('image/')) {
      alert('画像ファイルを選択してください');
      return;
    }

    // ファイルサイズチェック（5MB以下）
    if (file.size > 5 * 1024 * 1024) {
      alert('画像サイズは5MB以下にしてください');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.onload = () => {
        // Canvas を使って画像をリサイズ・圧縮
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // 最大サイズを設定（幅・高さ）
        const maxSize = 400;
        let width = img.width;
        let height = img.height;

        // アスペクト比を保ちながらリサイズ
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // 画像を描画
        ctx?.drawImage(img, 0, 0, width, height);

        // JPEG形式で圧縮（品質80%）
        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.8);

        console.log('画像圧縮完了:', {
          元サイズ: file.size,
          圧縮後サイズ: compressedBase64.length,
          圧縮率: Math.round((1 - compressedBase64.length / file.size) * 100) + '%'
        });

        setThumbnailPreview(compressedBase64);
        setFormData({ ...formData, thumbnail_url: compressedBase64 });
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };

  const removeThumbnail = () => {
    setThumbnailPreview(null);
    setFormData({ ...formData, thumbnail_url: undefined });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddStaff = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.username || !formData.password || !formData.name) {
      alert('必須項目を入力してください');
      return;
    }

    if (formData.password.length < 6) {
      alert('パスワードは6文字以上で設定してください');
      return;
    }

    if (formData.pin && formData.pin.length < 3) {
      alert('PINコードは3桁以上で設定してください');
      return;
    }

    try {
      const staffData = {
        username: formData.username,
        password: formData.password,
        displayName: formData.name,  // nameをdisplayNameに変換
        roleLevel: formData.role_level,  // role_levelをroleLevelに変換
        email: formData.email || '',
        phone: formData.phone || '',
        hireDate: formData.hire_date || '',
        thumbnail_url: formData.thumbnail_url,
        icon: formData.icon,
        icon_color: formData.icon_color,
        enable_pin_login: formData.enable_pin_login,
        pin: formData.pin,
        permissions: formData.role_level === 0 ? formData.permissions : []
      };

      const apiClient = createAuthAxios();
      const response = await apiClient.post('/staff', staffData);

      if (response.data.success) {
        alert('スタッフを追加しました');
        setShowAddForm(false);
        resetFormData();
        fetchStaffList();
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'スタッフの追加に失敗しました');
    }
  };

  const handleUpdateStaff = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedStaff) return;

    try {
      const updateData: any = {
        username: formData.username,
        displayName: formData.name,  // nameをdisplayNameに変換
        roleLevel: Number(formData.role_level),  // 数値に変換
        email: formData.email || '',
        phone: formData.phone || '',
        hireDate: formData.hire_date || '',
        icon: formData.icon,
        thumbnail_url: formData.thumbnail_url,
        icon_color: formData.icon_color,
        enable_pin_login: formData.enable_pin_login,
        isActive: true  // activeをisActiveに変換
      };

      console.log('送信するデータ:', updateData);
      console.log('thumbnail_url長さ:', updateData.thumbnail_url ? updateData.thumbnail_url.length : 0);

      if (formData.pin) {
        updateData.pin = formData.pin;
      }

      if (formData.role_level === 0) {
        updateData.permissions = formData.permissions;
      }

      const apiClient = createAuthAxios();
      const response = await apiClient.put(`/staff/${selectedStaff.id}`, updateData);

      if (response.data.success) {
        alert('スタッフ情報を更新しました');
        setShowEditForm(false);
        setSelectedStaff(null);
        resetFormData();
        fetchStaffList();
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'スタッフ情報の更新に失敗しました');
    }
  };

  const handleToggleActive = async (staffId: number, currentStatus: boolean) => {
    const action = currentStatus ? '無効化' : '有効化';
    if (!confirm(`このスタッフを${action}しますか？`)) return;

    try {
      const apiClient = createAuthAxios();
      // スタッフの詳細を取得
      const detailsResponse = await apiClient.get(`/staff/${staffId}`);
      const staffDetails = detailsResponse.data.data;

      // 必須フィールドを含めて更新
      const response = await apiClient.put(`/staff/${staffId}`, {
        username: staffDetails.username,
        displayName: staffDetails.displayName,
        isActive: !currentStatus
      });

      if (response.data.success) {
        alert(`スタッフを${action}しました`);
        fetchStaffList();
      }
    } catch (error: any) {
      alert(error.response?.data?.message || `スタッフの${action}に失敗しました`);
    }
  };

  const handleDeleteStaff = async (staffId: number, isPermanentDelete: boolean = false) => {
    const message = isPermanentDelete
      ? 'このスタッフを完全削除しますか？この操作は取り消せません。'
      : 'このスタッフを削除しますか？この操作は取り消せません。';

    if (!confirm(message)) return;

    try {
      const apiClient = createAuthAxios();
      const response = await apiClient.delete(`/staff/${staffId}`);
      if (response.data.success) {
        const successMessage = isPermanentDelete ? 'スタッフを完全削除しました' : 'スタッフを削除しました';
        alert(successMessage);
        fetchStaffList();
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'スタッフの削除に失敗しました');
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordResetForm.newPassword !== passwordResetForm.confirmPassword) {
      alert('パスワードが一致しません');
      return;
    }

    if (passwordResetForm.newPassword.length < 6) {
      alert('パスワードは6文字以上で設定してください');
      return;
    }

    try {
      const apiClient = createAuthAxios();
      const response = await apiClient.put(`/staff/${passwordResetForm.staffId}`, {
        password: passwordResetForm.newPassword
      });

      if (response.data.success) {
        alert('パスワードをリセットしました');
        setPasswordResetForm({ staffId: 0, newPassword: '', confirmPassword: '' });
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'パスワードのリセットに失敗しました');
    }
  };

  const handlePinReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (pinResetForm.newPin !== pinResetForm.confirmPin) {
      alert('PINが一致しません');
      return;
    }

    if (pinResetForm.newPin.length < 3) {
      alert('PINは3桁以上で設定してください');
      return;
    }

    try {
      const apiClient = createAuthAxios();
      const response = await apiClient.put(`/staff/${pinResetForm.staffId}/reset-pin`, {
        newPin: pinResetForm.newPin
      });

      if (response.data.success) {
        alert('PINをリセットしました');
        setPinResetForm({ staffId: 0, newPin: '', confirmPin: '' });
        fetchStaffList();
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'PINのリセットに失敗しました');
    }
  };

  const openEditForm = async (staff: Staff) => {
    const details = await fetchStaffDetails(staff.id);
    if (!details) {
      alert('スタッフ情報の取得に失敗しました');
      return;
    }

    setSelectedStaff(details);
    setFormData({
      username: details.username,
      password: '',
      name: details.displayName || details.display_name || details.name,
      role_level: details.roleLevel || details.role_level,
      pin: '',
      icon: details.icon || '👤',
      icon_color: details.icon_color || '#3B82F6',
      thumbnail_url: details.thumbnail_url,
      enable_pin_login: details.enable_pin_login,
      permissions: details.permissions || []
    });
    setThumbnailPreview(details.thumbnail_url || null);
    setShowEditForm(true);
  };

  const resetFormData = () => {
    setFormData({
      username: '',
      password: '',
      name: '',
      role_level: 0,
      pin: '',
      icon: '👤',
      icon_color: '#3B82F6',
      thumbnail_url: undefined,
      enable_pin_login: true,
      permissions: []
    });
    setThumbnailPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePermissionChange = (pagePath: string, canAccess: boolean) => {
    const newPermissions = [...formData.permissions];
    const existingIndex = newPermissions.findIndex(p => p.page_path === pagePath);

    if (existingIndex >= 0) {
      newPermissions[existingIndex].can_access = canAccess;
    } else {
      newPermissions.push({ page_path: pagePath, can_access: canAccess });
    }

    setFormData({ ...formData, permissions: newPermissions });
  };

  const handleApplyTemplate = async (templateId: number) => {
    try {
      // テンプレート詳細を取得
      const response = await apiClient.get(`/permissions/templates/${templateId}`);
      if (response.data.success && response.data.data) {
        const template = response.data.data;

        // テンプレートの権限をformDataに反映
        const newPermissions: Permission[] = template.permissions.map((perm: PermissionItem) => ({
          page_path: perm.route_path,
          can_access: true
        }));

        setFormData({ ...formData, permissions: newPermissions });
        setSelectedTemplate(templateId);

        console.log(`テンプレート「${template.template_name}」を適用しました（${newPermissions.length}個の権限）`);
      }
    } catch (error) {
      console.error('テンプレート適用エラー:', error);
      alert('テンプレートの適用に失敗しました');
    }
  };

  const isPageAccessible = (pagePath: string) => {
    const permission = formData.permissions.find(p => p.page_path === pagePath);
    return permission ? permission.can_access : false;
  };

  const getRoleLevelLabel = (level: number) => {
    switch (level) {
      case 3: return '開発者';
      case 2: return 'オーナー';
      case 0: return 'スタッフ';
      default: return '不明';
    }
  };

  const getRoleLevelColor = (level: number) => {
    switch (level) {
      case 3: return 'bg-purple-100 text-purple-800';
      case 2: return 'bg-blue-100 text-blue-800';
      case 0: return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ja-JP');
  };

  return (
    <PageContainer>
      <div>
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Users className="h-8 w-8 text-blue-600" />
            スタッフ管理（PIN対応版）
          </h1>
          <div className="flex gap-2">
            <button
              onClick={() => setShowInactive(!showInactive)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                showInactive
                  ? 'bg-gray-600 text-white hover:bg-gray-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              title={showInactive ? '無効スタッフを非表示' : '無効スタッフを表示'}
            >
              {showInactive ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              {showInactive ? '無効スタッフ表示中' : '無効スタッフを表示'}
            </button>
            <button
              onClick={() => navigate('/home')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Home className="w-5 h-5" />
              ホーム
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <UserPlus className="h-5 w-5" />
              スタッフ追加
            </button>
          </div>
        </div>

        {/* タブナビゲーション */}
        <div className="mb-6 border-b">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('list')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'list'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              スタッフ一覧
            </button>
          </nav>
        </div>

        {/* スタッフ一覧タブ */}
        {activeTab === 'list' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : (
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      アイコン
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      スタッフ名
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ユーザー名
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      権限
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      PIN
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ステータス
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      最終更新
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {staffList
                    .filter(staff => {
                      const isActive = staff.isActive ?? staff.active;
                      // active = -1 のスタッフは完全に非表示
                      if (isActive === -1) return false;
                      // 無効スタッフ表示フラグに応じてフィルタ
                      return showInactive || isActive;
                    })
                    .map((staff) => (
                    <tr key={staff.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {staff.thumbnail_url ? (
                          <img
                            src={staff.thumbnail_url}
                            alt={staff.displayName || staff.display_name}
                            className="w-12 h-12 rounded-full object-cover border-2"
                            style={{ borderColor: staff.icon_color }}
                          />
                        ) : (
                          <div
                            className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                            style={{ backgroundColor: staff.icon_color + '20' }}
                          >
                            {staff.icon || '👤'}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {staff.displayName || staff.display_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {staff.username}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          getRoleLevelColor(Number(staff.roleLevel ?? staff.role_level))
                        }`}>
                          {getRoleLevelLabel(Number(staff.roleLevel ?? staff.role_level))}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {staff.enable_pin_login ? (
                          staff.has_pin ? (
                            <span className="text-green-600 flex items-center gap-1">
                              <Key className="h-4 w-4" />
                              設定済
                            </span>
                          ) : (
                            <span className="text-gray-400">未設定</span>
                          )
                        ) : (
                          <span className="text-gray-300">無効</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          (staff.isActive ?? staff.active)
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {(staff.isActive ?? staff.active) ? '有効' : '無効'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDateTime(staff.updated_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditForm(staff)}
                            className="text-blue-600 hover:text-blue-900"
                            title="編集"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setPinResetForm({ ...pinResetForm, staffId: staff.id })}
                            className="text-purple-600 hover:text-purple-900"
                            title="PINリセット"
                          >
                            <Key className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(staff.id, staff.isActive ?? staff.active)}
                            className={(staff.isActive ?? staff.active) ? "text-yellow-600 hover:text-yellow-900" : "text-green-600 hover:text-green-900"}
                            title={(staff.isActive ?? staff.active) ? "無効化" : "有効化"}
                          >
                            <UserCheck className="h-4 w-4" />
                          </button>
                          {staff.username !== 'admin' && (
                            <button
                              onClick={() => handleDeleteStaff(staff.id, !(staff.isActive ?? staff.active))}
                              className="text-red-600 hover:text-red-900"
                              title={(staff.isActive ?? staff.active) ? "削除" : "完全削除"}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* 権限管理タブ */}
        {activeTab === 'permissions' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Shield className="h-5 w-5" />
                権限レベル説明
              </h2>
              <div className="space-y-4">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-semibold text-purple-800">開発者 (レベル3)</h3>
                  <ul className="mt-2 text-sm text-gray-600 space-y-1">
                    <li>• 全機能へのフルアクセス</li>
                    <li>• 開発者ツールへのアクセス</li>
                    <li>• システム設定の変更</li>
                    <li>• 全スタッフの管理</li>
                    <li>• Admin管理・機能フラグ管理</li>
                  </ul>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="font-semibold text-blue-800">オーナー (レベル2)</h3>
                  <ul className="mt-2 text-sm text-gray-600 space-y-1">
                    <li>• 店舗運営に必要な全機能</li>
                    <li>• スタッフ管理</li>
                    <li>• 売上分析・レポート</li>
                    <li>• メニュー管理</li>
                  </ul>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="font-semibold text-green-800">スタッフ (レベル0)</h3>
                  <ul className="mt-2 text-sm text-gray-600 space-y-1">
                    <li>• 個別に設定された画面のみアクセス可能</li>
                    <li>• POSレジ操作</li>
                    <li>• 自分の勤怠管理</li>
                    <li>• その他許可された機能</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* アイコン一覧タブ */}
        {activeTab === 'icons' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Palette className="h-5 w-5" />
              利用可能なアイコン
            </h2>
            <div className="grid grid-cols-8 gap-4">
              {availableIcons.map((icon, index) => (
                <div
                  key={index}
                  className="w-16 h-16 rounded-lg border-2 border-gray-200 flex items-center justify-center text-3xl hover:border-blue-500 cursor-pointer"
                >
                  {icon}
                </div>
              ))}
            </div>
            <h3 className="text-lg font-semibold mt-6 mb-4">アイコンカラー</h3>
            <div className="flex gap-4">
              {iconColors.map((color, index) => (
                <div
                  key={index}
                  className="w-12 h-12 rounded-full border-2 border-gray-300"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>
        )}

        {/* スタッフ追加フォーム */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8">
              <h2 className="text-xl font-semibold mb-4">新規スタッフ追加</h2>

              <form onSubmit={handleAddStaff}>
                <div className="grid grid-cols-2 gap-4">
                  {/* 左側：基本情報 */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-700 border-b pb-2">基本情報</h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ユーザー名 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        パスワード <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                        required
                        minLength={6}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        表示名 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        権限レベル
                      </label>
                      <select
                        value={formData.role_level}
                        onChange={(e) => setFormData({ ...formData, role_level: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        <option value={0}>スタッフ</option>
                        <option value={2}>オーナー</option>
                        <option value={3}>開発者</option>
                      </select>
                    </div>
                  </div>

                  {/* 右側：PIN・アイコン設定 */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-700 border-b pb-2">PIN・アイコン設定</h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        PINコード（3〜8文字） <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="text"
                        value={formData.pin}
                        onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md font-mono text-lg tracking-widest"
                        placeholder="数字 + 記号（♠♥♦♣〇△□☆）"
                        required
                        minLength={3}
                        maxLength={8}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        プロフィール画像 / アイコン
                      </label>
                      <div className="space-y-3">
                        {/* サムネイル表示とアップロード */}
                        <div className="flex items-center gap-4">
                          {thumbnailPreview || formData.thumbnail_url ? (
                            <div className="relative">
                              <img
                                src={thumbnailPreview || formData.thumbnail_url}
                                alt="プロフィール"
                                className="w-20 h-20 rounded-full object-cover border-2 border-gray-300"
                              />
                              <button
                                type="button"
                                onClick={removeThumbnail}
                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <div
                              className="w-20 h-20 rounded-full border-2 border-gray-300 flex items-center justify-center text-3xl cursor-pointer hover:border-blue-500"
                              style={{ backgroundColor: formData.icon_color + '20' }}
                              onClick={() => fileInputRef.current?.click()}
                            >
                              {formData.icon}
                            </div>
                          )}
                          <div className="flex flex-col gap-2">
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1"
                            >
                              <Upload className="h-4 w-4" />
                              画像をアップロード
                            </button>
                            {!thumbnailPreview && !formData.thumbnail_url && (
                              <button
                                type="button"
                                onClick={() => setShowIconPicker(!showIconPicker)}
                                className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200"
                              >
                                アイコン選択
                              </button>
                            )}
                          </div>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                        </div>

                        {showIconPicker && (
                          <div className="mt-2 p-3 border rounded-md max-h-32 overflow-y-auto">
                            <div className="grid grid-cols-6 gap-2">
                              {availableIcons.map((icon, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => {
                                    setFormData({ ...formData, icon });
                                    setShowIconPicker(false);
                                  }}
                                  className="w-10 h-10 rounded hover:bg-gray-100 flex items-center justify-center text-xl"
                                >
                                  {icon}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        アイコンカラー
                      </label>
                      <div className="flex gap-2">
                        {iconColors.slice(0, 5).map((color, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => setFormData({ ...formData, icon_color: color })}
                            className={`w-8 h-8 rounded-full border-2 ${
                              formData.icon_color === color ? 'border-blue-500' : 'border-gray-300'
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* 記号入力のヘルプ */}
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="text-sm text-blue-800">
                        <div className="font-semibold mb-2">💡 記号の入力方法</div>
                        <div className="space-y-1">
                          <div>以下の記号をコピー＆ペーストして使用できます：</div>
                          <div className="flex gap-2 mt-2 p-2 bg-white rounded border border-blue-300">
                            <button
                              type="button"
                              onClick={() => navigator.clipboard.writeText('♠♥♦♣〇△□☆')}
                              className="text-xl px-3 py-1 bg-blue-100 hover:bg-blue-200 rounded"
                              title="クリックでコピー"
                            >
                              ♠ ♥ ♦ ♣ 〇 △ □ ☆
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ページアクセス権限（スタッフの場合のみ） */}
                {formData.role_level === 0 && (
                  <div className="mt-6 space-y-4">
                    <h3 className="font-semibold text-gray-700 border-b pb-2">
                      <CheckSquare className="inline h-4 w-4 mr-1" />
                      ページアクセス権限
                    </h3>

                    {/* テンプレート選択 */}
                    {permissionTemplates.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-blue-800 mb-2">
                          テンプレートから選択
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                          {permissionTemplates.map((template) => (
                            <button
                              key={template.id}
                              type="button"
                              onClick={() => handleApplyTemplate(template.id)}
                              className="flex items-center gap-2 px-3 py-2 bg-white border border-blue-300 rounded-lg hover:bg-blue-100 text-left"
                            >
                              <span className="text-xl">{template.icon}</span>
                              <div>
                                <div className="text-sm font-semibold text-gray-800">
                                  {template.template_name}
                                </div>
                                <div className="text-xs text-gray-600">
                                  {template.description}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* カテゴリ別権限チェックボックス */}
                    {permissionItems.length > 0 ? (
                      <div className="space-y-4">
                        {['core', 'management', 'line', 'admin'].map((category) => {
                          const categoryItems = permissionItems.filter(
                            (item) => item.category === category && !item.requires_developer
                          );
                          if (categoryItems.length === 0) return null;

                          const categoryLabels: Record<string, string> = {
                            core: '基本機能',
                            management: '管理機能',
                            line: 'LINE連携',
                            admin: 'システム設定'
                          };

                          return (
                            <div key={category} className="border border-gray-200 rounded-lg p-3">
                              <h5 className="font-semibold text-gray-700 mb-2">
                                {categoryLabels[category]}
                              </h5>
                              <div className="grid grid-cols-3 gap-2">
                                {categoryItems.map((item) => (
                                  <label key={item.id} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={isPageAccessible(item.route_path)}
                                      onChange={(e) =>
                                        handlePermissionChange(item.route_path, e.target.checked)
                                      }
                                      className="mr-2"
                                    />
                                    <span className="text-sm flex items-center gap-1">
                                      <span>{item.icon}</span>
                                      <span>{item.display_name}</span>
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-gray-500 text-sm">
                        権限項目を読み込み中...
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      resetFormData();
                    }}
                    className="px-4 py-2 border rounded-md hover:bg-gray-50"
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    追加
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* スタッフ編集フォーム */}
        {showEditForm && selectedStaff && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8">
              <h2 className="text-xl font-semibold mb-4">スタッフ情報編集</h2>

              <form onSubmit={handleUpdateStaff}>
                <div className="grid grid-cols-2 gap-4">
                  {/* 左側：基本情報 */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-700 border-b pb-2">基本情報</h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ユーザー名
                      </label>
                      <input
                        type="text"
                        value={formData.username}
                        className="w-full px-3 py-2 border rounded-md bg-gray-100"
                        disabled
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        表示名 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        権限レベル
                      </label>
                      <select
                        value={formData.role_level}
                        onChange={(e) => setFormData({ ...formData, role_level: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        <option value={0}>スタッフ</option>
                        <option value={2}>オーナー</option>
                        <option value={3}>開発者</option>
                      </select>
                    </div>
                  </div>

                  {/* 右側：PIN・アイコン設定 */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-700 border-b pb-2">PIN・アイコン設定</h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        新しいPINコード（変更する場合のみ）
                      </label>
                      <input
                        type="text"
                        inputMode="text"
                        value={formData.pin}
                        onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md font-mono text-lg tracking-widest"
                        placeholder="変更しない場合は空欄"
                        maxLength={8}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        プロフィール画像 / アイコン
                      </label>
                      <div className="space-y-3">
                        {/* サムネイル表示とアップロード */}
                        <div className="flex items-center gap-4">
                          {thumbnailPreview || formData.thumbnail_url ? (
                            <div className="relative">
                              <img
                                src={thumbnailPreview || formData.thumbnail_url}
                                alt="プロフィール"
                                className="w-20 h-20 rounded-full object-cover border-2 border-gray-300"
                              />
                              <button
                                type="button"
                                onClick={removeThumbnail}
                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <div
                              className="w-20 h-20 rounded-full border-2 border-gray-300 flex items-center justify-center text-3xl cursor-pointer hover:border-blue-500"
                              style={{ backgroundColor: formData.icon_color + '20' }}
                              onClick={() => fileInputRef.current?.click()}
                            >
                              {formData.icon}
                            </div>
                          )}
                          <div className="flex flex-col gap-2">
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1"
                            >
                              <Upload className="h-4 w-4" />
                              画像をアップロード
                            </button>
                            {!thumbnailPreview && !formData.thumbnail_url && (
                              <button
                                type="button"
                                onClick={() => setShowIconPicker(!showIconPicker)}
                                className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200"
                              >
                                アイコン選択
                              </button>
                            )}
                          </div>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                        </div>

                        {showIconPicker && (
                          <div className="mt-2 p-3 border rounded-md max-h-32 overflow-y-auto">
                            <div className="grid grid-cols-6 gap-2">
                              {availableIcons.map((icon, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => {
                                    setFormData({ ...formData, icon });
                                    setShowIconPicker(false);
                                  }}
                                  className="w-10 h-10 rounded hover:bg-gray-100 flex items-center justify-center text-xl"
                                >
                                  {icon}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        アイコンカラー
                      </label>
                      <div className="flex gap-2">
                        {iconColors.slice(0, 5).map((color, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => setFormData({ ...formData, icon_color: color })}
                            className={`w-8 h-8 rounded-full border-2 ${
                              formData.icon_color === color ? 'border-blue-500' : 'border-gray-300'
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* 記号入力のヘルプ */}
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="text-sm text-blue-800">
                        <div className="font-semibold mb-2">💡 記号の入力方法</div>
                        <div className="space-y-1">
                          <div>以下の記号をコピー＆ペーストして使用できます：</div>
                          <div className="flex gap-2 mt-2 p-2 bg-white rounded border border-blue-300">
                            <button
                              type="button"
                              onClick={() => navigator.clipboard.writeText('♠♥♦♣〇△□☆')}
                              className="text-xl px-3 py-1 bg-blue-100 hover:bg-blue-200 rounded"
                              title="クリックでコピー"
                            >
                              ♠ ♥ ♦ ♣ 〇 △ □ ☆
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ページアクセス権限（スタッフの場合のみ） */}
                {formData.role_level === 0 && (
                  <div className="mt-6 space-y-4">
                    <h3 className="font-semibold text-gray-700 border-b pb-2">
                      <CheckSquare className="inline h-4 w-4 mr-1" />
                      ページアクセス権限
                    </h3>

                    {/* テンプレート選択 */}
                    {permissionTemplates.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-blue-800 mb-2">
                          テンプレートから選択
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                          {permissionTemplates.map((template) => (
                            <button
                              key={template.id}
                              type="button"
                              onClick={() => handleApplyTemplate(template.id)}
                              className="flex items-center gap-2 px-3 py-2 bg-white border border-blue-300 rounded-lg hover:bg-blue-100 text-left"
                            >
                              <span className="text-xl">{template.icon}</span>
                              <div>
                                <div className="text-sm font-semibold text-gray-800">
                                  {template.template_name}
                                </div>
                                <div className="text-xs text-gray-600">
                                  {template.description}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* カテゴリ別権限チェックボックス */}
                    {permissionItems.length > 0 ? (
                      <div className="space-y-4">
                        {['core', 'management', 'line', 'admin'].map((category) => {
                          const categoryItems = permissionItems.filter(
                            (item) => item.category === category && !item.requires_developer
                          );
                          if (categoryItems.length === 0) return null;

                          const categoryLabels: Record<string, string> = {
                            core: '基本機能',
                            management: '管理機能',
                            line: 'LINE連携',
                            admin: 'システム設定'
                          };

                          return (
                            <div key={category} className="border border-gray-200 rounded-lg p-3">
                              <h5 className="font-semibold text-gray-700 mb-2">
                                {categoryLabels[category]}
                              </h5>
                              <div className="grid grid-cols-3 gap-2">
                                {categoryItems.map((item) => (
                                  <label key={item.id} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={isPageAccessible(item.route_path)}
                                      onChange={(e) =>
                                        handlePermissionChange(item.route_path, e.target.checked)
                                      }
                                      className="mr-2"
                                    />
                                    <span className="text-sm flex items-center gap-1">
                                      <span>{item.icon}</span>
                                      <span>{item.display_name}</span>
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-gray-500 text-sm">
                        権限項目を読み込み中...
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditForm(false);
                      setSelectedStaff(null);
                    }}
                    className="px-4 py-2 border rounded-md hover:bg-gray-50"
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    更新
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* PINリセットフォーム */}
        {pinResetForm.staffId > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-semibold mb-4">PINリセット</h2>
              <form onSubmit={handlePinReset}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      新しいPIN <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={pinResetForm.newPin}
                      onChange={(e) => setPinResetForm({ ...pinResetForm, newPin: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                      minLength={3}
                      maxLength={6}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PIN確認 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={pinResetForm.confirmPin}
                      onChange={(e) => setPinResetForm({ ...pinResetForm, confirmPin: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                      minLength={3}
                      maxLength={6}
                    />
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setPinResetForm({ staffId: 0, newPin: '', confirmPin: '' })}
                    className="px-4 py-2 border rounded-md hover:bg-gray-50"
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                  >
                    リセット
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* パスワードリセットフォーム */}
        {passwordResetForm.staffId > 0 && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h2 className="text-xl font-semibold mb-4">パスワードリセット</h2>
              <form onSubmit={handlePasswordReset}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      新しいパスワード <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={passwordResetForm.newPassword}
                      onChange={(e) => setPasswordResetForm({ ...passwordResetForm, newPassword: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                      minLength={6}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      パスワード確認 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={passwordResetForm.confirmPassword}
                      onChange={(e) => setPasswordResetForm({ ...passwordResetForm, confirmPassword: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md"
                      required
                      minLength={6}
                    />
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setPasswordResetForm({ staffId: 0, newPassword: '', confirmPassword: '' })}
                    className="px-4 py-2 border rounded-md hover:bg-gray-50"
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
                  >
                    リセット
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default StaffManagementEnhanced;